import { tr } from "../i18n/LanguageContext";
import React, { useEffect, useState } from "react";
import { Loader2, XCircle, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const UNDO_SECONDS = 300;

const toMs = (iso) => Date.parse(/Z$|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);

const secondsLeft = (pendingAt) => Math.max(0, Math.ceil((toMs(pendingAt) + UNDO_SECONDS * 1000 - Date.now()) / 1000));

const useSecondsLeft = (pendingAt) => {
  const [left, setLeft] = useState(() => secondsLeft(pendingAt));
  useEffect(() => {
    setLeft(secondsLeft(pendingAt));
    const t = setInterval(() => setLeft(secondsLeft(pendingAt)), 1000);
    return () => clearInterval(t);
  }, [pendingAt]);
  return left;
};

export const CancelEventButton = ({ eventId, onCancelled }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const submit = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/events/${eventId}/cancel`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: reason.trim() || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : tr("Не удалось отменить событие"));
      setOpen(false);
      onCancelled(data);
    } catch (e) {
      toast({ title: tr("Ошибка"), description: e.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="cancel-event-btn" className="eunoia-btn-secondary flex items-center gap-2" style={{ color: "#b4534a" }}>
        <XCircle className="h-4 w-4" /> {tr("Отменить событие")}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cancel-event-dialog">
          <DialogHeader>
            <DialogTitle>{tr("Отменить событие?")}</DialogTitle>
            <DialogDescription>
              {tr("Событие сразу скроется из каталога. В течение 5 минут отмену можно вернуть — после этого участники получат уведомление и письмо.")}
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 500))}
            className="eunoia-input min-h-[100px] resize-y"
            placeholder={tr("Комментарий для участников (необязательно)")}
            data-testid="cancel-event-reason"
          />
          <p className="text-xs text-right" style={{ color: "var(--eunoia-soft)" }}>{reason.length}/500</p>
          <DialogFooter className="gap-2">
            <button onClick={() => setOpen(false)} className="eunoia-btn-secondary" data-testid="cancel-event-back">{tr("Назад")}</button>
            <button onClick={submit} disabled={busy} className="eunoia-btn-primary flex items-center gap-2" data-testid="cancel-event-confirm">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {tr("Отменить событие")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const CancelPendingBanner = ({ eventId, pendingAt, isOrganizer, onRestored }) => {
  const left = useSecondsLeft(pendingAt);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const restore = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/events/${eventId}/restore`, { method: "POST", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : tr("Не удалось вернуть событие"));
      toast({ title: tr("Событие восстановлено") });
      onRestored();
    } catch (e) {
      toast({ title: tr("Ошибка"), description: e.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const mmss = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`;
  return (
    <div className="rounded-2xl p-5 flex items-center gap-4 flex-wrap" style={{ backgroundColor: "#fbeeee", color: "#8a3b34" }} data-testid="cancel-pending-banner">
      <XCircle className="h-5 w-5 shrink-0" />
      <div className="flex-1 min-w-[220px]">
        <p className="font-medium">{tr("Событие отменено")}</p>
        {isOrganizer && (
          <p className="text-sm" data-testid="cancel-countdown">
            {left > 0 ? `${tr("Участники получат уведомление через")} ${mmss}. ${tr("До этого отмену можно вернуть.")}` : tr("Участники уведомлены.")}
          </p>
        )}
      </div>
      {isOrganizer && left > 0 && (
        <button onClick={restore} disabled={busy} className="eunoia-btn-secondary flex items-center gap-2" data-testid="restore-event-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} {tr("Вернуть событие")}
        </button>
      )}
    </div>
  );
};
